<script setup lang="ts">
import type { Project } from '~/utils/project/Project'
import { computed, ref, watch } from 'vue'
import Loading from '~/components/loading/index.vue'
import { useFormalizationReadOnly } from '~/composables/financing/useFormalizationReadOnly'
import useCombosHardware from '~/store-v2/installation/useCombosHardware'

interface Props {
  isOpen: boolean
  project: Project | null
  initialTab?: 'benefits' | 'inverters'
}

const props = withDefaults(defineProps<Props>(), {
  initialTab: 'benefits',
})

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
}>()

const { t } = useI18n()

// Provided by the formalization step on terminal financing; defaults to false elsewhere
const isFormalizationReadOnly = useFormalizationReadOnly()

const projectId = computed(() => props.project?.id || '')
const systemType = computed(() => props.project?.system_type || null)

const isModalOpen = ref(props.isOpen)
const selectedTab = ref(props.initialTab)

watch(() => props.isOpen, (newValue) => {
  isModalOpen.value = newValue
})

const tabListModel = ref([
  {
    key: 'benefits',
    label: t('simulation.result.modals.combo_connect.tabs.benefits'),
    active: props.initialTab === 'benefits',
  },
  {
    key: 'inverters',
    label: t('simulation.result.modals.combo_connect.tabs.inverters'),
    active: props.initialTab === 'inverters',
  },
])

const {
  data: combosData,
  isLoading: isLoadingCombos,
} = useCombosHardware(
  projectId,
  systemType.value,
  true,
  {
    enabled: computed(() => props.isOpen && (selectedTab.value === 'inverters' || props.initialTab === 'inverters') && !!projectId.value),
  },
)

function handleTabChange(tab: { key: string }) {
  selectedTab.value = tab.key as 'benefits' | 'inverters'
  tabListModel.value = tabListModel.value.map(t => ({
    ...t,
    active: t.key === tab.key,
  }))
}

watch(() => props.initialTab, (newTab) => {
  if (newTab) {
    selectedTab.value = newTab
    tabListModel.value = tabListModel.value.map(t => ({
      ...t,
      active: t.key === newTab,
    }))
  }
}, { immediate: true })

function closeModal() {
  isModalOpen.value = false
  emit('update:isOpen', false)
}

const inverters = computed(() => {
  if (!combosData.value?.inverters) {
    return []
  }

  return combosData.value.inverters.map((inverter, index) => ({
    id: `inverter-${index}`,
    marca: inverter.brand || '',
    modelo: inverter.name || '',
    potencia: inverter.power,
  }))
})

const invertersSearch = ref('')
const filteredInverters = computed(() => {
  const query = invertersSearch.value.trim().toLowerCase()
  if (!query)
    return inverters.value

  return inverters.value.filter(inverter =>
    inverter.marca.toLowerCase().includes(query)
    || inverter.modelo.toLowerCase().includes(query)
    || String(inverter.potencia ?? '').toLowerCase().includes(query),
  )
})

const inverterHeaders = {
  brand: {
    text: t('simulation.result.modals.combo_connect.inverters.list.brand'),
    width: 'auto',
    highlight: true,
  },
  model: {
    text: t('simulation.result.modals.combo_connect.inverters.list.name'),
    width: 'auto',
    highlight: true,
  },
  power: {
    text: t('simulation.result.modals.combo_connect.inverters.list.power'),
    width: 'auto',
    highlight: true,
  },
}

const inverterPositions = {
  desktop: ['marca', 'modelo', 'potencia'],
  mobile: [['marca'], ['modelo'], ['potencia']],
}

function goToStore() {
  if (isFormalizationReadOnly.value)
    return

  const url = import.meta.env.MODE === 'production'
    ? 'https://loja.solfacil.com.br/'
    : 'https://stg-loja.solfacil.com.br/'
  window.open(url, '_self')
}

const benefitsList = computed(() => [
  t('simulation.result.modals.combo_connect.benefits.items.one'),
  t('simulation.result.modals.combo_connect.benefits.items.two'),
  t('simulation.result.modals.combo_connect.benefits.items.three'),
  t('simulation.result.modals.combo_connect.benefits.items.four'),
])
</script>

<template>
  <SolModal
    id="combo-conecte-only-modal"
    :is-open="isModalOpen"
    :title="t('simulation.result.modals.combo_connect.title')"
    :action-primary-text="t('simulation.result.modals.combo_connect.button_ok')"
    :size="{
      desktop: 'large',
      mobile: 'bottom-sheet',
    }"
    data-testid="combo-conecte-only-modal"
    @close="closeModal"
    @action:primary="closeModal"
  >
    <div class="flex flex-col gap-6">
      <SolTabs
        id="combo-conecte-tabs"
        v-model:tab-list="tabListModel"
        @tabitem:active="handleTabChange"
      >
        <template #tab:benefits>
          <div class="flex flex-col gap-6">
            <div class="text-left">
              <p class="fonts-body-medium-regular text-neutral-low-medium">
                {{ t('simulation.result.modals.combo_connect.benefits.description') }}
              </p>
            </div>

            <div>
              <ul class="list-disc">
                <li
                  v-for="(benefit, index) in benefitsList"
                  :key="index"
                >
                  {{ benefit }}
                </li>
              </ul>
            </div>

            <SolAlert
              id="combo-conecte-important"
              feedback="informative"
              :title="t('simulation.result.modals.combo_connect.important.title')"
              class="w-full"
            >
              <div class="fonts-body-small-regular flex flex-col gap-1">
                <span>
                  {{ t('simulation.result.modals.combo_connect.important.items.one.text') }}
                  <span class="font-bold underline">{{ t('simulation.result.modals.combo_connect.important.items.one.highlight') }}</span>
                </span>
                <span>{{ t('simulation.result.modals.combo_connect.important.items.two.text') }}
                  <span class="font-bold underline">{{ t('simulation.result.modals.combo_connect.important.items.two.highlight') }}</span>
                </span>
                <span>
                  {{ t('simulation.result.modals.combo_connect.important.items.three.text_before') }}
                  <span class="font-bold underline">
                    {{ t('simulation.result.modals.combo_connect.important.items.three.highlight') }}
                  </span>{{ t('simulation.result.modals.combo_connect.important.items.three.text_after') }}
                </span>
              </div>
            </SolAlert>
          </div>
        </template>

        <template #tab:inverters>
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-4">
              <div class="flex flex-col md:site:flex-row md:site:items-center md:site:justify-between gap-2">
                <p class="fonts-body-medium-regular text-neutral-low-medium">
                  {{ t('simulation.result.modals.combo_connect.inverters.description') }}
                </p>
                <SolButton
                  id="go-to-store-button"
                  variant="secondary"
                  size="small"
                  class="store-button"
                  :disabled="isFormalizationReadOnly"
                  @click="goToStore"
                >
                  {{ t('simulation.result.modals.combo_connect.inverters.store_button') }}
                </SolButton>
              </div>

              <SolAlert
                id="combo-conecte-inverters-alert"
                feedback="informative"
                class="w-full"
              >
                <div class="fonts-body-medium-regular text-neutral-low-medium">
                  {{ t('simulation.result.modals.combo_connect.inverters.alert.description') }}
                </div>
              </SolAlert>
            </div>

            <SolInputText
              v-if="!isLoadingCombos && inverters.length > 0"
              id="inverters-search"
              v-model="invertersSearch"
              name="inverters-search"
              class="w-full"
              placeholder="Buscar por marca, modelo ou potência"
            />

            <div v-if="isLoadingCombos" class="flex justify-center items-center py-8">
              <Loading />
            </div>
            <div v-else-if="filteredInverters.length === 0" class="flex flex-col items-center justify-center py-8">
              <SolEmptyState
                id="no-inverters"
                variant="empty-list"
                :title="t('simulation.result.modals.combo_connect.inverters.empty.title')"
                :subtitle="t('simulation.result.modals.combo_connect.inverters.empty.subtitle')"
              />
            </div>
            <div v-else>
              <SolList
                id="compatible-inverters-list"
                data-testid="compatible-inverters-list"
                class="inverters-list"
                :loading="isLoadingCombos"
                aria-label="Lista de inversores compatíveis"
                fallback-value="-"
                :hide-menu="true"
                :data="filteredInverters"
                :headers="inverterHeaders"
                :sort-positions="inverterPositions"
              >
                <template #value:marca="{ data }">
                  <span class="fonts-body-medium-bold text-neutral-low-dark">
                    {{ data.value || '-' }}
                  </span>
                </template>

                <template #value:modelo="{ data }">
                  <span class="fonts-body-small-regular text-neutral-low-medium">
                    {{ data.value || '-' }}
                  </span>
                </template>

                <template #value:potencia="{ data }">
                  <span class="fonts-body-small-regular text-neutral-low-light">
                    {{ data.value ? `${data.value} kW` : '-' }}
                  </span>
                </template>
              </SolList>
            </div>
          </div>
        </template>
      </SolTabs>
    </div>
  </SolModal>
</template>

<style scoped lang="scss">
.list-disc {
  @apply list-inside flex flex-col;
  @apply fonts-body-medium-regular text-neutral-low-medium;

  li:first-child {
    @apply font-bold;
  }
}

.store-button {
  white-space: nowrap;
}

.inverters-list {
  width: 100%;

  :deep(.sol-list-core) {
    width: 100%;
  }
}
</style>
